import type {
  MyPenaltyListQuery,
  PenaltyEntry,
  PenaltyEntryStatus,
} from '@/features/penalties/types/penalty.types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  usePenaltiesWorkspace,
  usePenaltyTypes,
} from '@/features/penalties/hooks/usePenalties'

import { AlertTriangle, Plus } from 'lucide-react'
import {
  useWorkspaceCompanyFilter,
  WorkspaceCompanyChips,
} from '@/features/companies/components/WorkspaceCompanyFilter'
import { PageShell } from '@/components/common/PageShell'
import {
  WorkspaceToolbar,
  WorkspaceToolbarSearch,
} from '@/components/common/WorkspaceToolbar'
import { Button } from '@/components/ui'
import { usePermissions } from '@/hooks/usePermissions'
import { CreatePenaltyModal } from './CreatePenaltyModal'
import { PenaltyDetailModal } from './PenaltyDetailModal'
import { PenaltyEntryTable } from './PenaltyEntryTable'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const

export function WorkspacePenaltiesPage(): React.ReactElement {
  const { can } = usePermissions()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [status, setStatus] = useState<string>('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const filter = useWorkspaceCompanyFilter({ onChange: () => setPage(1) })
  const [selectedEntry, setSelectedEntry] = useState<PenaltyEntry | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const query = useMemo<MyPenaltyListQuery>(
    () => ({
      page,
      limit,
      status: status ? (status as PenaltyEntryStatus) : undefined,
      search: debouncedSearch || undefined,
      companyIds: filter.companyIds,
    }),
    [page, limit, status, debouncedSearch, filter.companyIds]
  )

  const listQuery = usePenaltiesWorkspace(query)
  // Fetch penalty types only for the company of the row currently being viewed.
  const typesQuery = usePenaltyTypes(selectedEntry?.companyId ?? null)

  const rows = useMemo(() => listQuery.data?.data ?? [], [listQuery.data?.data])
  const total = listQuery.data?.pagination?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const canCreatePenalty = can('penalties', 'create')
  const selectedCompanyIds = Array.from(filter.selectedCompanyIds)
  const createCompanyId =
    selectedCompanyIds.length === 1
      ? selectedCompanyIds[0]
      : filter.companies.length === 1
        ? filter.companies[0]._id
        : null

  const handleStatusChange = useCallback((s: string) => {
    setStatus(s)
    setPage(1)
  }, [])

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(search.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [search])

  const handleRowClick = useCallback(
    (entryId: string) => {
      const found = rows.find((r) => r._id === entryId)
      if (found) setSelectedEntry(found)
    },
    [rows]
  )

  return (
    <>
      <PageShell
        breadcrumbItems={[
          { label: 'Payroll', to: '/payroll' },
          { label: 'Penalties' },
        ]}
        icon={AlertTriangle}
        title="Penalties"
        description="Cross-company penalty records across every company you can read."
        className="gap-0"
        hideHeader
      >
        <div className="space-y-4">
          <WorkspaceToolbar
            filter={<WorkspaceCompanyChips filter={filter} />}
            actions={
              canCreatePenalty ? (
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setCreateOpen(true)}
                  disabled={!createCompanyId}
                  title={createCompanyId ? 'Issue penalty' : 'Select one company first'}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  <span className="ml-1.5">Issue penalty</span>
                </Button>
              ) : null
            }
          >
            <WorkspaceToolbarSearch
              value={search}
              onChange={setSearch}
              placeholder="Search name or email…"
              ariaLabel="Search penalties"
              active={Boolean(debouncedSearch)}
            />
          </WorkspaceToolbar>

          <PenaltyEntryTable
            entries={rows}
            types={[]}
            isLoading={listQuery.isLoading}
            error={listQuery.error as Error | null}
            statusFilter={status}
            onStatusFilterChange={handleStatusChange}
            onRowClick={handleRowClick}
            onRetry={() => listQuery.refetch()}
            showCompany
            showEmployee
            companyNameById={filter.companyNameById}
            emptyMessage="No penalty records in this filter."
          />

          <TablePaginationBar
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={setLimit}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
          />
        </div>
      </PageShell>

      <PenaltyDetailModal
        companyId={selectedEntry?.companyId ?? ''}
        entry={selectedEntry}
        types={typesQuery.data ?? []}
        onClose={() => setSelectedEntry(null)}
      />

      {createCompanyId && (
        <CreatePenaltyModal
          companyId={createCompanyId}
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </>
  )
}

WorkspacePenaltiesPage.displayName = 'WorkspacePenaltiesPage'
